import { buildVehiclePageMetadata, getPublicPageMetadata } from './publicPageMetadata';

interface PublicRoute {
  path?: string;
  name?: unknown;
  params?: Record<string, unknown>;
}

export interface PublicPageView {
  event: 'gokey_page_view';
  page_path: string;
  page_location: string;
  page_title: string;
  page_referrer: string;
}

interface TrackingDependencies {
  origin: () => string;
  referrer: () => string;
  push: (event: PublicPageView) => void;
  schedule: (callback: () => void, delay: number) => unknown;
  cancel: (handle: unknown) => void;
}

interface PendingView {
  path: string;
  location: string;
  title: string;
  referrer: string;
  sent: boolean;
  timer?: unknown;
}

// Root-mounted hash SPA. Do not use fullPath/query, route-name titles or raw referrers.
function publicPath(route: PublicRoute): string | null {
  if (route.path === '/home' || route.path === '/catalogo') return route.path;
  const id = route.params?.id;
  if (route.name === 'car-detail' && typeof id === 'string' && /^\d+$/.test(id)
      && route.path === '/detalle/' + id) return route.path;
  return null;
}

function safeReferrer(value: string, origin: string): string {
  try {
    const url = new URL(value);
    const route = url.hash.split('?')[0];
    if (url.origin === origin && /^#\/(home|catalogo|detalle\/\d+)$/.test(route)) {
      return origin + '/' + route;
    }
  } catch { /* No raw or private referrer is sent. */ }
  return '';
}

export function createPublicPageTracker(deps: TrackingDependencies, detailWaitMs = 15000) {
  let active: PendingView | null = null;
  let currentPath: string | null = null;
  let previousLocation = '';
  let hasNavigated = false;

  function emit(view: PendingView): void {
    if (view.sent || active !== view) return;
    view.sent = true;
    if (view.timer !== undefined) deps.cancel(view.timer);
    previousLocation = view.location;
    // A broken/blocked analytics consumer must never reject navigation or vehicle loading.
    try {
      deps.push({ event: 'gokey_page_view', page_path: view.path, page_location: view.location,
        page_title: view.title, page_referrer: view.referrer });
    } catch { /* No retry that might duplicate an already consumed event. */ }
  }

  function flush(): void { if (active) emit(active); }

  function navigate(route: PublicRoute): void {
    const nextPath = publicPath(route);
    if (nextPath && nextPath === currentPath) return; // Query/filter-only changes are not new screens.
    flush(); // An unfinished detail gets its captured fallback before the next screen, never lost/reordered.
    currentPath = nextPath;
    active = null;
    if (!nextPath) {
      previousLocation = ''; // Do not report private/auth/business-flow paths as virtual referrers.
      hasNavigated = true;
      return;
    }
    const metadata = getPublicPageMetadata(route);
    if (!metadata) return;
    const origin = new URL(deps.origin()).origin;
    if (!/^https?:\/\//.test(origin)) return;
    const view: PendingView = {
      path: nextPath, location: origin + '/#' + nextPath, title: metadata.title,
      referrer: previousLocation || (!hasNavigated ? safeReferrer(deps.referrer(), origin) : ''), sent: false,
    };
    active = view;
    hasNavigated = true;
    if (route.name === 'car-detail') {
      view.timer = deps.schedule(() => emit(view), detailWaitMs);
    } else {
      emit(view);
    }
  }

  function vehicleReady(route: PublicRoute, title: string): void {
    if (!active || active.sent || publicPath(route) !== active.path || route.name !== 'car-detail') return;
    active.title = title;
    emit(active);
  }

  return { navigate, vehicleReady, flush };
}

const publicTracker = createPublicPageTracker({
  origin: () => window.location.origin,
  referrer: () => document.referrer,
  push: event => {
    const target = window as Window & { dataLayer?: unknown };
    if (target.dataLayer === undefined) target.dataLayer = [];
    // Preserve GTM's existing queue and patched push; don't overwrite an invalid foreign object.
    if (Array.isArray(target.dataLayer)) target.dataLayer.push(event);
  },
  schedule: (callback, delay) => setTimeout(callback, delay),
  cancel: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
});

export function trackPublicNavigation(route: PublicRoute): void {
  try { publicTracker.navigate(route); } catch { /* Analytics must not interfere with navigation. */ }
}

export function trackPublicVehicleReady(route: PublicRoute, requestedId: string, vehicle: unknown): void {
  if (route.name !== 'car-detail' || route.params?.id !== requestedId) return;
  try { publicTracker.vehicleReady(route, buildVehiclePageMetadata(vehicle).title); }
  catch { /* Vehicle rendering/metadata retain their existing behavior. */ }
}

if (typeof window !== 'undefined') {
  // A visitor leaving before the detail request resolves still contributes one fallback view.
  window.addEventListener('pagehide', () => publicTracker.flush());
}
